import { useContext, useRef, useState } from "react";
import type { EChartsOption } from "echarts";
import { Box, CircularProgress, Container } from "@mui/material";
import DashboardCardHeader from "../DashboardCardHeader";
import { RopChart } from "../../../../main/components/RopChart";
import { getYearDay } from "../../../../main/themes/defaultLayout";
import { DashboardContext } from "../../context/DashboardContexProvider";
import { BaseErrorCard } from "../../models/dados-indicadores.model";
import { EmptyTable } from "../../../clients/components/EmptyState";
import { CardTypesEnum } from "../../types";
import { getCardIsLoading } from "../TextCardContent";

export type LossDiagramProps = {
  cardId?: string;
  title?: string;
  lastUpdate: Date;
  cardType: number;
  data: {
    energiaIdeal: number;
    perdaNoCircuito: number;
    perdaDoTrafo: number;
    perdaDosInversores: number;
    perdaPorIndisponibilidade: number;
    outrasPerdas: number;
    energiaGerada: number;
    lastCollectTime?: string;
    cardError: BaseErrorCard;
  };
};

export default function LossDiagramChart(props: LossDiagramProps) {
  const chartLossDiagram = useRef(null);
  const { data, title, lastUpdate, cardType } = props;
  const [isMouseIn, setIsMouseIn] = useState<boolean>(false);
  const headerTitle = title ?? "Diagrama de Perdas (MWh)";

  const {
    cardsDisplayableColorProps,
    setCardsDisplayableColorProps,
    cardIsLoading,
    requestParamCards,
  } = useContext(DashboardContext);

  const [energiaColor, setEnergiaColor] = useState<string>(
    cardsDisplayableColorProps.diagramaPerdas.energiaColor ?? "#FADE0A"
  );
  const [perdaColor, setPerdaColor] = useState<string>(
    cardsDisplayableColorProps.diagramaPerdas.perdaColor ?? "#EE7D00"
  );

  const timeout = 120;
  const now = new Date();
  const delay = lastUpdate ? (now.getTime() - lastUpdate.getTime()) / 1000 : 0;
  const hasDelay = delay > timeout;

  /**
   * Convenção: valor negativo em "outras perdas" = outros ganhos (barra verde para cima).
   * Valor positivo = perda adicional (barra para baixo, série Perdas).
   */
  const outrosGanhosParaCima = data.outrasPerdas < 0;
  const ganhoOutrasColor = "#2e7d32";

  const acumuladoAposIndisponibilidade =
    data.energiaIdeal -
    data.perdaNoCircuito -
    data.perdaDosInversores -
    data.perdaDoTrafo -
    data.perdaPorIndisponibilidade;

  const outrasPlaceholder =
    outrosGanhosParaCima
      ? acumuladoAposIndisponibilidade
      : acumuladoAposIndisponibilidade + data.outrasPerdas;

  // Ordem de exibição dos dados no gráfico
  const ordemEixoXGrafico = [
    "Energia\nIdeal", //"Energia Ideal",
    "Perdas\nPrim", //"Perdas no Primárias",
    "Perda\nInver.", //"Perda Dos Inversores",
    "Perda\nTrafo", //"Perda Do Trafo",
    "Perda\nIndisp.", //"Perda Por Indisponibilidade",
    outrosGanhosParaCima ? "Outros\nGanhos" : "Outras\nPerdas",
    "Energia\nGerada", //"Energia Gerada",
  ];

  const energiaEixoYGrafico: (
    | string
    | number
    | {
      value: number;
      itemStyle: { color: string; borderColor: string };
    }
  )[] = [
      data.energiaIdeal,
      "-",
      "-",
      "-",
      "-",
      outrosGanhosParaCima
        ? {
          value: Math.abs(data.outrasPerdas),
          itemStyle: {
            color: ganhoOutrasColor,
            borderColor: ganhoOutrasColor,
          },
        }
        : "-",
      data.energiaGerada,
    ];

  const perdasEixoYGrafico = [
    "-",
    data.perdaNoCircuito,
    data.perdaDosInversores,
    data.perdaDoTrafo,
    data.perdaPorIndisponibilidade,
    outrosGanhosParaCima
      ? "-"
      : data.outrasPerdas > 0
        ? data.outrasPerdas
        : "-",
    "-",
  ];

  const dadosEixoYDiferencaGrafico = [
    0, // energiaIdeal sempre 0
    data.energiaIdeal - data.perdaNoCircuito,
    data.energiaIdeal - data.perdaNoCircuito - data.perdaDosInversores,

    data.energiaIdeal -
    data.perdaNoCircuito -
    data.perdaDosInversores -
    data.perdaDoTrafo,

    data.energiaIdeal -
    data.perdaNoCircuito -
    data.perdaDosInversores -
    data.perdaDoTrafo -
    data.perdaPorIndisponibilidade,

    outrasPlaceholder,

    0, // energiaGerada sempre 0
  ];
  const collectDay = data.lastCollectTime
    ? getYearDay(data.lastCollectTime)
    : "";

  const numericFromAxisValue = (v: unknown): number | null => {
    if (typeof v === "number") {
      return v;
    }
    if (
      v &&
      typeof v === "object" &&
      "value" in v &&
      typeof (v as { value: unknown }).value === "number"
    ) {
      return (v as { value: number }).value;
    }
    return null;
  };

  // https://echarts.apache.org/examples/en/editor.html?c=bar-waterfall&lang=ts
  // https://echarts.apache.org/handbook/en/how-to/chart-types/bar/waterfall/
  const option: EChartsOption = {
    title: {
      text: collectDay,
      textStyle: {
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter(params: any) {
        const tar1 = params[1]; // Energia
        const tar2 = params[2]; // Perdas

        if (tar1.value !== "-") {
          const n = numericFromAxisValue(tar1.value);
          const tar1ValuePtBr =
            n !== null
              ? n.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
              : `${tar1.value}`;
          const isGanhoOutras = tar1.dataIndex === 5 && outrosGanhosParaCima;
          return `${tar1.name}<br/>${isGanhoOutras ? "Outros ganhos" : tar1.seriesName
            } : ${isGanhoOutras ? "+" : ""}${tar1ValuePtBr}`;
        } else {
          const tar2ValuePtBr =
            typeof tar2.value === "number"
              ? tar2.value.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
              : `${tar2.value}`;
          return `${tar2.name}<br/>${tar2.seriesName} : -${tar2ValuePtBr}`;
        }
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      top: "50",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      splitLine: { show: false },
      data: ordemEixoXGrafico,
    },
    yAxis: {
      type: "value",
    },
    series: [
      {
        name: "Placeholder",
        type: "bar",
        stack: "Total",
        itemStyle: {
          borderColor: "transparent",
          color: "transparent",
        },
        emphasis: {
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
        },
        data: dadosEixoYDiferencaGrafico,
      },
      {
        name: "Energia",
        type: "bar",
        stack: "Total",
        itemStyle: {
          borderColor: energiaColor,
          color: energiaColor,
        },
        label: {
          show: true,
          position: "inside",
          formatter(params): string {
            const raw = params.value;
            const num = numericFromAxisValue(raw);
            if (num === null) {
              return `${raw}`;
            }
            const txt = num.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
            return params.dataIndex === 5 && outrosGanhosParaCima ? `+${txt}` : txt;
          },
        },
        data: energiaEixoYGrafico,
      },
      {
        name: "Perdas",
        type: "bar",
        stack: "Total",
        itemStyle: {
          borderColor: perdaColor,
          color: perdaColor,
        },
        label: {
          show: true,
          position: "inside",
          formatter(params): string {
            return typeof params.value === "number"
              ? "-" +
              params.value.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
              : `${params.value}`;
          },
        },
        data: perdasEixoYGrafico,
      },
    ],
  };

  const [openModal, setopenModal] = useState<boolean>(false);
  const handleConfirmModal = () => {
    setCardsDisplayableColorProps({
      ...cardsDisplayableColorProps,
      diagramaPerdas: {
        energiaColor: energiaColor,
        perdaColor: perdaColor,
      },
    });
    setopenModal(false);
  };

  const hasData =
    data.energiaGerada ||
    data.energiaIdeal ||
    data.outrasPerdas ||
    data.perdaDoTrafo ||
    data.perdaDosInversores ||
    data.perdaNoCircuito ||
    data.perdaPorIndisponibilidade;

  return (
    <>
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseOver={() => setIsMouseIn(true)}
        onMouseLeave={() => setIsMouseIn(false)}
      >
        <DashboardCardHeader
          cardError={data.cardError}
          hasDelay={hasDelay}
          lastUpdate={lastUpdate}
          cardId={props.cardId}
          elementRef={chartLossDiagram}
          isMouseIn={isMouseIn}
          title={headerTitle}
          titleFontSize={21}
          openColorsModal={openModal}
          setOpenColorsModal={setopenModal}
          colorButton
          onConfirmColorsModal={handleConfirmModal}
          colorsOptions={[
            {
              title: "Energias",
              value: energiaColor,
              setValue: setEnergiaColor,
            },
            {
              title: "Perdas",
              value: perdaColor,
              setValue: setPerdaColor,
            },
          ]}
          cardType={CardTypesEnum.DiagramaPerdas}
          hasFilter
        />
        {!getCardIsLoading(cardType, cardIsLoading, requestParamCards) ? (
          hasData ? (
            <Container
              ref={chartLossDiagram}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: "90%",
              }}
            >
              <RopChart option={option} />
            </Container>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyTable title="Nenhum dado encontrado!" />
            </Box>
          )
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </div>
    </>
  );
}
